import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Plus,
  PencilSimple,
  Trash,
  CurrencyInr,
} from "@phosphor-icons/react";
import api, { cleanPayload, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const EMPTY = {
  class_id: "",
  head: "",
  amount: "",
  due_date: "",
  frequency: "annual",
  description: "",
};

const FREQUENCIES = [
  { v: "annual", label: "Annual" },
  { v: "monthly", label: "Monthly" },
];

// Phase 19C — frontend tolerance layer mirroring backend
// `fee_service.normalize_frequency`. Legacy values (one_time / quarterly /
// null) all render as "annual" in the UI. Stored value remains untouched
// until the row is edited and re-saved.
const normalizeFreq = (f) => (f === "monthly" ? "monthly" : "annual");
const FREQ_LABEL = { monthly: "Monthly", annual: "Annual" };

function StructureDialog({ open, onOpenChange, initial, classes, defaultClassId, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              ...EMPTY,
              ...initial,
              amount: String(initial.amount ?? ""),
              due_date: initial.due_date || "",
              // Map legacy one_time/quarterly to annual for the dropdown
              frequency: normalizeFreq(initial.frequency),
            }
          : { ...EMPTY, class_id: defaultClassId || "" },
      );
      setError("");
    }
  }, [open, initial, defaultClassId]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const amt = parseFloat(form.amount);
      if (Number.isNaN(amt) || amt < 0) throw new Error("Amount must be a non-negative number");
      await onSubmit(cleanPayload({ ...form, amount: amt }));
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || formatApiError(err));
    }
    setSubmitting(false);
  };

  const isEdit = Boolean(initial?.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="structure-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Edit fee head" : "Add fee head"}
          </DialogTitle>
          <DialogDescription>
            Fees are scoped to a class within a session. Same head name cannot repeat per class+session.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {!isEdit && (
            <div>
              <Label>Class *</Label>
              <Select value={form.class_id} onValueChange={(v) => setForm((f) => ({ ...f, class_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="structure-class-select">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`structure-class-option-${c.id}`}>
                      {c.name}{c.section ? ` - ${c.section}` : ""}
                      <span className="text-slate-400 ml-2">· {c.session_name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Head *</Label>
            <Input
              required
              value={form.head}
              onChange={update("head")}
              placeholder="e.g. Tuition Fee, Transport, Lab"
              className="mt-1.5"
              data-testid="structure-head-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={update("amount")}
                className="mt-1.5 font-mono"
                data-testid="structure-amount-input"
              />
            </div>
            <div>
              <Label>Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => setForm((f) => ({ ...f, frequency: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="structure-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Due date</Label>
            <Input type="date" value={form.due_date} onChange={update("due_date")} className="mt-1.5" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={form.description || ""} onChange={update("description")} className="mt-1.5" placeholder="Optional" />
          </div>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={submitting || (!isEdit && !form.class_id)} className="bg-blue-900 hover:bg-blue-800" data-testid="structure-submit-btn">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function FeeStructure() {
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [filterSession, setFilterSession] = useState("__all__");
  const [filterClass, setFilterClass] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filteredClasses = useMemo(() => {
    if (filterSession === "__all__") return classes;
    return classes.filter((c) => c.session_id === filterSession);
  }, [classes, filterSession]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSession !== "__all__") params.set("session_id", filterSession);
      if (filterClass !== "__all__") params.set("class_id", filterClass);
      const url = `/school/fee-structures${params.toString() ? `?${params.toString()}` : ""}`;
      const { data } = await api.get(url);
      setStructures(data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [filterSession, filterClass]);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/school/classes"), api.get("/school/sessions")]);
        setClasses(c.data);
        setSessions(s.data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (payload) => {
    const { data } = await api.post("/school/fee-structures", payload);
    setStructures((s) => [data, ...s]);
    toast.success(`Added '${data.head}'`);
  };
  const edit = async (payload) => {
    const id = dialog.initial.id;
    const { data } = await api.patch(`/school/fee-structures/${id}`, payload);
    setStructures((s) => s.map((x) => (x.id === data.id ? data : x)));
    toast.success("Fee head updated");
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/school/fee-structures/${confirmDelete.id}`);
      setStructures((s) => s.filter((x) => x.id !== confirmDelete.id));
      toast.success("Fee head deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setConfirmDelete(null);
  };

  const activeSession = sessions.find((s) => s.is_active);
  const defaultClassId = useMemo(() => {
    if (activeSession) return classes.find((c) => c.session_id === activeSession.id)?.id || "";
    return classes[0]?.id || "";
  }, [classes, activeSession]);

  const totalPerClass = useMemo(() => {
    const map = new Map();
    structures.forEach((s) => {
      map.set(s.class_id, (map.get(s.class_id) || 0) + s.amount);
    });
    return map;
  }, [structures]);

  return (
    <div className="space-y-6" data-testid="fee-structure-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Finance</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading mt-1">
            Fee Structure <span className="text-slate-400 font-normal">({structures.length})</span>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Configure the fees due per class. Once payments exist for a head, the amount can still be edited but the head can't be deleted.
          </p>
        </div>
        <Button
          onClick={() => setDialog({ open: true, initial: null })}
          disabled={classes.length === 0}
          data-testid="add-structure-btn"
          className="bg-blue-900 hover:bg-blue-800 gap-2"
        >
          <Plus size={16} weight="bold" /> Add fee head
        </Button>
      </div>

      {classes.length === 0 && (
        <div className="p-4 border border-amber-200 bg-amber-50 text-amber-800 rounded-md text-sm">
          You need at least one class before adding fees. Go to <strong>Classes</strong> first.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <Select value={filterSession} onValueChange={(v) => { setFilterSession(v); setFilterClass("__all__"); }}>
            <SelectTrigger className="w-44 h-9" data-testid="structure-filter-session"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sessions</SelectItem>
              {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-52 h-9" data-testid="structure-filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All classes</SelectItem>
              {filteredClasses.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.section ? ` - ${c.section}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filterClass !== "__all__" && (
            <div className="ml-auto text-sm text-slate-600">
              <span className="text-slate-400 mr-1">Class total:</span>
              <span className="font-mono font-semibold text-slate-900">
                {formatINR(totalPerClass.get(filterClass) || 0)}
              </span>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="structure-table">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-3">Head</th>
                <th className="text-left font-semibold px-4 py-3">Class</th>
                <th className="text-right font-semibold px-4 py-3">Amount</th>
                <th className="text-left font-semibold px-4 py-3">Frequency</th>
                <th className="text-left font-semibold px-4 py-3">Due date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && structures.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
              ) : structures.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-12 py-12 text-center" data-testid="structure-empty">
                    <CurrencyInr size={32} className="mx-auto text-slate-300 mb-2" />
                    <div className="text-sm text-slate-500">
                      {classes.length === 0
                        ? "Create a class first."
                        : "No fee heads yet for this filter. Click 'Add fee head' to create one."}
                    </div>
                  </td>
                </tr>
              ) : (
                structures.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`structure-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{s.head}</div>
                      {s.description && <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="text-sm">{s.class_name || "—"}</div>
                      <div className="text-xs text-slate-500">{s.session_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-semibold text-slate-900">{formatINR(s.amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">{FREQ_LABEL[normalizeFreq(s.frequency)]}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">{s.due_date || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setDialog({ open: true, initial: s })}
                          data-testid={`edit-structure-${s.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                          title="Edit"
                        >
                          <PencilSimple size={15} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(s)}
                          data-testid={`delete-structure-${s.id}`}
                          className="p-1.5 rounded hover:bg-red-50 text-red-600"
                          title="Delete"
                        >
                          <Trash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StructureDialog
        open={dialog.open}
        onOpenChange={(v) => setDialog({ open: v, initial: v ? dialog.initial : null })}
        initial={dialog.initial}
        classes={classes}
        defaultClassId={defaultClassId}
        onSubmit={dialog.initial ? edit : create}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete fee head?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <strong>{confirmDelete?.head}</strong> ({formatINR(confirmDelete?.amount || 0)}) from this class. Heads with existing payments cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-structure">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
